import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface MenuItem {
  id: string;
  name: string;
  path: string;
  icon: string;
}

interface MenuGroup {
  id: string;
  name: string;
  icon: string;
  items: MenuItem[];
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['console']);

  const menuGroups: MenuGroup[] = [
    {
      id: 'console',
      name: '管理控制台',
      icon: 'ri-dashboard-line',
      items: [
        { id: 'home', name: '源文档管理', path: '/', icon: 'ri-file-list-3-line' },
        { id: 'agents', name: '智能体接入', path: '/agents', icon: 'ri-robot-line' },
        { id: 'statistics', name: '数据统计', path: '/statistics', icon: 'ri-bar-chart-line' }
      ]
    },
    {
      id: 'apps',
      name: '应用中心',
      icon: 'ri-apps-line',
      items: [
        { id: 'applications', name: '应用中心', path: '/applications', icon: 'ri-grid-line' }
      ]
    }
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <aside
      className={`bg-[#1B3568] text-white transition-all duration-300 flex flex-col ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Logo区域 */}
      <div className="h-16 flex items-center justify-between px-5 border-b border-blue-900/30">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm whitespace-nowrap">"全知"业务数字化治理平台</span>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-8 h-8 flex items-center justify-center hover:bg-blue-800/50 rounded-md transition-colors cursor-pointer"
        >
          <i className={`ri-${collapsed ? 'menu-unfold' : 'menu-fold'}-line text-xl`}></i>
        </button>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <div className="space-y-2 px-3">
          {menuGroups.map((group) => {
            const isExpanded = expandedGroups.includes(group.id);
            return (
              <div key={group.id}>
                {/* 一级菜单 */}
                <button
                  onClick={() => !collapsed && toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-blue-100 hover:bg-blue-700/50 hover:text-white transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${group.icon} text-lg`}></i>
                    </div>
                    {!collapsed && <span className="text-sm font-medium whitespace-nowrap">{group.name}</span>}
                  </div>
                  {!collapsed && group.items.length > 0 && (
                    <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-base transition-transform`}></i>
                  )}
                </button>

                {/* 二级菜单 */}
                {!collapsed && isExpanded && group.items.length > 0 && (
                  <ul className="mt-1 space-y-1 ml-3">
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <li key={item.id}>
                          <a
                            onClick={() => handleNavigation(item.path)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                            }`}
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${item.icon} text-base`}></i>
                            </div>
                            <span className="text-sm whitespace-nowrap">{item.name}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* 底部用户信息 */}
      <div className="p-4 border-t border-blue-900/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-user-line text-lg"></i>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">管理员</div>
              <div className="text-xs text-blue-200 truncate">admin@company.com</div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
